import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  Checkbox,
  Divider,
  Badge,
  Collapse,
  Alert,
  Snackbar,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";

// ─── Config ──────────────────────────────────────────────────────────────────
const QUESTION_TYPES = [
  { value: "ALL",       label: "All Types",       icon: "🗂️" },
  { value: "MCQ",       label: "Multiple Choice", icon: "📝" },
  { value: "FIB",       label: "Fill in Blank",   icon: "✏️" },
  { value: "TRUEFALSE", label: "True / False",    icon: "✅" },
  { value: "IMAGE",     label: "Image Based",     icon: "🖼️" },
];

const SORT_OPTIONS = [
  { value: "name_asc",   label: "Name: A → Z" },
  { value: "name_desc",  label: "Name: Z → A" },
  { value: "count_desc", label: "Most Questions" },
  { value: "count_asc",  label: "Least Questions" },
];

export default function FlashPractice() {
  const { mode } = useContext(ThemeContext);
  const navigate  = useNavigate();
  const token     = sessionStorage.getItem("authToken");
  const apiUrl    = process.env.REACT_APP_BASE_URL;

  // ── Theme shortcuts ───────────────────────────────────────────────────────
  const isDark        = mode === "dark";
  const accent        = "#1e8baf";
  const textPrimary   = isDark ? "#f0f4f8" : "#1a2a3a";
  const textSecondary = isDark ? "#888"     : "#999";
  const cardBg        = isDark
    ? "linear-gradient(145deg,#1a2230 0%,#1e2a38 100%)"
    : "linear-gradient(145deg,#ffffff 0%,#f8fbfd 100%)";

  // ── Data ─────────────────────────────────────────────────────────────────
  const [folders,         setFolders]         = useState([]);
  const [isLoading,       setIsLoading]       = useState(false);
  const [availableTags,   setAvailableTags]   = useState([]);
  const [isFetchingTags,  setIsFetchingTags]  = useState(false);
  const [isFetchingQ,     setIsFetchingQ]     = useState(false);

  // ── Selection ────────────────────────────────────────────────────────────
  const [selectedFolderIds, setSelectedFolderIds] = useState([]);
  const [expandedFolders,   setExpandedFolders]   = useState({});
  const [selectedTagIds,    setSelectedTagIds]    = useState([]);
  const [activeQType,       setActiveQType]       = useState("ALL");

  // ── UI ────────────────────────────────────────────────────────────────────
  const [search,              setSearch]              = useState("");
  const [sort,                setSort]                = useState("name_asc");
  const [practiceDialogOpen,  setPracticeDialogOpen]  = useState(false);
  const [settingsOpen,        setSettingsOpen]        = useState(false);
  const [userGuideOpen,       setUserGuideOpen]       = useState(false);
  const [snackbar,            setSnackbar]            = useState({ open: false, message: "", severity: "info" });

  // ── Space repetition ──────────────────────────────────────────────────────
  const [spaceRepetitionEnabled, setSpaceRepetitionEnabled] = useState(false);
  const [reviewSettings, setReviewSettings] = useState({
    forgot_minutes:             1,
    recalled_with_effort_hours: 5,
    partially_recalled_hours:   4,
    easily_recalled_days:       1,
    skip_hours:                 1,
  });

  // ─── API: fetch folders ───────────────────────────────────────────────────
  const buildParentMap = (nodes, map = {}) => {
    if (!Array.isArray(nodes)) return map;
    for (const n of nodes) {
      if (n && typeof n.id !== "undefined") map[n.id] = n.parent ?? null;
      if (Array.isArray(n?.subfolders) && n.subfolders.length)
        buildParentMap(n.subfolders, map);
    }
    return map;
  };

  const fetchFolders = async () => {
    if (!token || !apiUrl) return;
    setIsLoading(true);
    try {
      const r1   = await fetch(`${apiUrl}/folders/`, { headers: { Authorization: `Bearer ${token}` } });
      const raw1 = await r1.json();
      const pMap = buildParentMap(Array.isArray(raw1) ? raw1 : [raw1], {});

      const r2   = await fetch(`${apiUrl}/questions/summary/`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r2.json();

      const proc = (f, isTop = false) => ({
        id:         f.folder_id,
        name:       f.folder_name,
        mcq:        f.mcq       || 0,
        fib:        f.fib       || 0,
        tf:         f.truefalse || 0,
        image:      f.image     || 0,
        all:        f.total     || 0,
        type:       f.type      || (isTop ? "folder" : "subfolder"),
        parent:     isTop ? null : pMap[f.folder_id] ?? null,
        subfolders: Array.isArray(f.subfolders) ? f.subfolders.map(sf => proc(sf, false)) : [],
      });

      const top = Array.isArray(data?.by_folder) ? data.by_folder : [];
      setFolders(top.map(f => proc(f, true)));
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to load folders", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReviewSettings = async () => {
    try {
      const res = await fetch(`${apiUrl}/review-settings/`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        const s    = Array.isArray(data) ? data[0] : data;
        setReviewSettings({
          forgot_minutes:             s?.forgot_minutes             ?? 1,
          recalled_with_effort_hours: s?.recalled_with_effort_hours ?? 5,
          partially_recalled_hours:   s?.partially_recalled_hours   ?? 4,
          easily_recalled_days:       s?.easily_recalled_days       ?? 1,
          skip_hours:                 s?.skip_hours                 ?? 1,
        });
      }
    } catch (e) { /* silent */ }
  };

  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${apiUrl}/review-settings/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(reviewSettings),
      });
      if (res.ok) {
        setSettingsOpen(false);
        setSnackbar({ open: true, message: "Settings saved!", severity: "success" });
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchFolders(); fetchReviewSettings(); }, []);

  // ─── Fetch tags when selection changes ───────────────────────────────────
  useEffect(() => {
    if (selectedFolderIds.length === 0) { setAvailableTags([]); setSelectedTagIds([]); return; }
    const go = async () => {
      setIsFetchingTags(true);
      try {
        const res  = await fetch(`${apiUrl}/tags/available/`, {
          method:  "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body:    JSON.stringify({ folder_ids: selectedFolderIds }),
        });
        if (res.ok) {
          const data = await res.json();
          const tags = Array.isArray(data) ? data : [];
          setAvailableTags(tags);
          setSelectedTagIds(prev => prev.filter(id => tags.some(t => t.id === id)));
        }
      } catch (e) { setAvailableTags([]); }
      finally { setIsFetchingTags(false); }
    };
    go();
  }, [selectedFolderIds]);

  // ─── Selection helpers ────────────────────────────────────────────────────
  const collectIds = (folder) => {
    const ids = [folder.id];
    (folder.subfolders || []).forEach(sf => ids.push(...collectIds(sf)));
    return ids;
  };

  const getAllIds = () => { const ids = []; folders.forEach(f => ids.push(...collectIds(f))); return ids; };

  const toggleFolder = (folder) => {
    const ids    = collectIds(folder);
    const allSel = ids.every(id => selectedFolderIds.includes(id));
    setSelectedFolderIds(prev =>
      allSel ? prev.filter(id => !ids.includes(id)) : [...new Set([...prev, ...ids])]
    );
  };

  const isSel   = (f) => collectIds(f).every(id => selectedFolderIds.includes(id));
  const isIndet = (f) => {
    const ids = collectIds(f);
    return ids.some(id => selectedFolderIds.includes(id)) && !ids.every(id => selectedFolderIds.includes(id));
  };

  // ─── API: start practice ──────────────────────────────────────────────────
 const handleStartPractice = async () => {
    if (selectedTagIds.length === 0) {
     setSnackbar({ open: true, message: "Please select at least one tag.", severity: "warning" });
    return;
  }
  if (selectedFolderIds.length === 0) {
    setSnackbar({ open: true, message: "Please select at least one folder", severity: "warning" });
    return;
  }
  setIsFetchingQ(true);
  try {
    const body = {
      folder_ids: selectedFolderIds,
      question_types: activeQType === "ALL" ? ["MCQ", "FIB", "TRUEFALSE", "IMAGE"] : [activeQType],
      ...(selectedTagIds.length > 0 && { tag_ids: selectedTagIds }),
    };

    console.log("API URL:", `${apiUrl}/questions/by-tags/`);  // 👈
    console.log("Request body:", body);                        // 👈
    console.log("Token:", token);                              // 👈

    const res = await fetch(`${apiUrl}/questions/by-tags/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });

    console.log("Response status:", res.status);              // 👈

    if (!res.ok) throw new Error("Failed to fetch questions");
    const data = await res.json();
    console.log("Response data:", data);                      // 👈

   const allQuestions = Object.entries(data).flatMap(([type, qs]) =>
  (Array.isArray(qs) ? qs : []).map((q) => ({
    ...q,
    question_type: type,
    questionType: type.toLowerCase() === "truefalse" ? "tf" : type.toLowerCase(),
    subfolder_id: q.subfolder_id || q.folder,
    file_url: q.file_url || q.image || null,
  }))
);
    if (allQuestions.length === 0) {
      setSnackbar({ open: true, message: "No questions found for selected filters", severity: "info" });
      return;
    }
    navigate("/allflash", {
  state: {
    questions: allQuestions,
    folderIds: selectedFolderIds,
    tagIds: selectedTagIds,
    questionType: activeQType,
    spaceRepetitionEnabled,
  },
});
    setPracticeDialogOpen(false);
  } catch (err) {
    console.error("Error:", err);                             // 👈
    setSnackbar({ open: true, message: err.message, severity: "error" });
  } finally {
    setIsFetchingQ(false);
  }
};

  const handleViewDueCards = () => {
    if (selectedFolderIds.length === 0) {
      setSnackbar({ open: true, message: "Please select at least one folder", severity: "warning" });
      return;
    }
navigate("/allflash", {
  state: {
    reviewMode: true,
    folderId: selectedFolderIds[0], // singular, matches AllFlash expectation
    folderIds: selectedFolderIds,   // keep for future use
    spaceRepetitionEnabled,
  },
});  };

  // ─── Filtered + sorted folders ────────────────────────────────────────────
  const displayFolders = (() => {
    let r = [...folders];
    if (search.trim()) r = r.filter(f => f.name.toLowerCase().includes(search.toLowerCase()));
    if (sort === "name_asc")   r.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === "name_desc")  r.sort((a, b) => b.name.localeCompare(a.name));
    if (sort === "count_desc") r.sort((a, b) => b.all - a.all);
    if (sort === "count_asc")  r.sort((a, b) => a.all - b.all);
    return r;
  })();

  const totalSelected = selectedFolderIds.length;

  // ─── Sub-components ───────────────────────────────────────────────────────

  // Small pill showing count + label
  const CountPill = ({ label, value, color }) => (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 0.5,
      px: 1.2, py: 0.35, borderRadius: "7px",
      background: isDark ? "rgba(255,255,255,0.05)" : `${color}14`,
    }}>
      <Typography sx={{ color, fontWeight: 700, fontSize: "11px", lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: textSecondary, fontSize: "11px", lineHeight: 1 }}>{label}</Typography>
    </Box>
  );

  // Subfolder row inside expanded card
  const SubfolderRow = ({ sf, depth = 0 }) => {
    const sel   = isSel(sf);
    const indet = isIndet(sf);
    const open  = expandedFolders[sf.id] || false;
    return (
      <>
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1,
          px: 1.5, py: 0.9,
          pl: `${(depth + 1) * 16 + 12}px`,
          borderRadius: "9px",
          background: sel
            ? isDark ? "rgba(30,139,175,0.14)" : "rgba(30,139,175,0.07)"
            : "transparent",
          "&:hover": { background: isDark ? "rgba(255,255,255,0.04)" : "rgba(30,139,175,0.05)" },
          transition: "background 0.18s",
        }}>
          <Checkbox
            checked={sel} indeterminate={indet}
            onChange={() => toggleFolder(sf)}
            onClick={e => e.stopPropagation()}
            size="small"
            sx={{
              p: 0.3,
              color: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.22)",
              "&.Mui-checked":              { color: accent },
              "&.MuiCheckbox-indeterminate":{ color: accent },
            }}
          />
          <FolderOpenIcon sx={{ color: "#ffbf00", fontSize: 17, flexShrink: 0 }} />
          <Typography variant="body2" sx={{
            flex: 1, fontWeight: 500, color: textPrimary, fontSize: "13px",
            cursor: sf.subfolders?.length ? "pointer" : "default",
          }}
            onClick={() => sf.subfolders?.length && setExpandedFolders(p => ({ ...p, [sf.id]: !p[sf.id] }))}>
            {sf.name}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.7, flexWrap: "wrap" }}>
            {sf.mcq   > 0 && <CountPill label="MCQ"   value={sf.mcq}   color="#1e8baf" />}
            {sf.fib   > 0 && <CountPill label="FIB"   value={sf.fib}   color="#9c27b0" />}
            {sf.tf    > 0 && <CountPill label="TF"    value={sf.tf}    color="#4caf50" />}
            {sf.image > 0 && <CountPill label="IMG"   value={sf.image} color="#0288d1" />}
            <CountPill label="All" value={sf.all} color={isDark ? "#90caf9" : "#455a64"} />
          </Box>
          {sf.subfolders?.length > 0 && (
            <ArrowDropDownIcon
              onClick={() => setExpandedFolders(p => ({ ...p, [sf.id]: !p[sf.id] }))}
              sx={{ color: isDark?"#90caf9":accent, fontSize:20, cursor:"pointer", ml:0.5,
                transform: open ? "rotate(180deg)" : "rotate(0)", transition:"transform 0.25s" }} />
          )}
        </Box>
        <Collapse in={open}>
          {sf.subfolders?.map(child => <SubfolderRow key={child.id} sf={child} depth={depth + 1} />)}
        </Collapse>
      </>
    );
  };

  // Main folder card
  const FolderCard = ({ folder, index }) => {
    const sel   = isSel(folder);
    const indet = isIndet(folder);
    const open  = expandedFolders[folder.id] || false;
    const hasSubs = folder.subfolders?.length > 0;

    return (
      <Grid item xs={12} md={6} lg={4}>
        <Paper elevation={0} sx={{
          p: 0, borderRadius: "20px", background: cardBg,
          border: sel
            ? `2px solid ${accent}`
            : isDark ? "2px solid rgba(255,255,255,0.06)" : "2px solid rgba(30,139,175,0.12)",
          boxShadow: isDark
            ? "0 8px 32px rgba(0,0,0,0.4),0 1px 0 rgba(255,255,255,0.04) inset"
            : "0 8px 32px rgba(30,139,175,0.08),0 1px 0 rgba(255,255,255,0.9) inset",
          transition: "transform 0.25s ease, box-shadow 0.25s ease, border 0.18s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: isDark
              ? "0 20px 48px rgba(0,0,0,0.5)"
              : "0 20px 48px rgba(30,139,175,0.14)",
          },
          overflow: "hidden",
        }}>
          {/* Accent bar */}
          <Box sx={{
            height: "3px",
            background: sel
              ? "linear-gradient(90deg,#1e3a8a 0%,#1e8baf 100%)"
              : `linear-gradient(90deg,#1e8baf ${index % 3 === 0 ? "0%" : "20%"},#4ec9e8 100%)`,
            opacity: 0.9,
          }} />

          <Box sx={{ p: 2.8 }}>
            {/* Title row */}
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flex: 1 }}>
                <Checkbox
                  checked={sel} indeterminate={indet}
                  onChange={() => toggleFolder(folder)}
                  onClick={e => e.stopPropagation()}
                  sx={{
                    p: 0.4, mt: "-1px",
                    color: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.22)",
                    "&.Mui-checked":              { color: accent },
                    "&.MuiCheckbox-indeterminate":{ color: accent },
                  }}
                />
                <Box
                  sx={{ flex: 1, cursor: hasSubs ? "pointer" : "default" }}
                  onClick={() => hasSubs && setExpandedFolders(p => ({ ...p, [folder.id]: !p[folder.id] }))}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    <FolderIcon sx={{ color: "#ffbf00", fontSize: 22, flexShrink: 0 }} />
                    <Typography variant="h6" sx={{
                      fontWeight: 700, color: textPrimary,
                      letterSpacing: "-0.02em", lineHeight: 1.3, fontSize: "15px",
                    }}>
                      {folder.name}
                    </Typography>
                    {hasSubs && (
                      <ArrowDropDownIcon sx={{
                        color: isDark ? "#90caf9" : accent,
                        transform: open ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.25s",
                        flexShrink: 0,
                      }} />
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Emoji icon */}
              <Box sx={{
                minWidth: 36, height: 36, borderRadius: "10px", fontSize: "17px",
                background: isDark ? "rgba(30,139,175,0.14)" : "rgba(30,139,175,0.07)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                📁
              </Box>
            </Box>

            {/* Count pills */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {folder.mcq   > 0 && <CountPill label="MCQ"   value={folder.mcq}   color="#1e8baf" />}
              {folder.fib   > 0 && <CountPill label="FIB"   value={folder.fib}   color="#9c27b0" />}
              {folder.tf    > 0 && <CountPill label="TF"    value={folder.tf}    color="#4caf50" />}
              {folder.image > 0 && <CountPill label="Image" value={folder.image} color="#0288d1" />}
              <Box sx={{
                display:"flex", alignItems:"center", gap:0.5,
                px:1.2, py:0.35, borderRadius:"7px",
                background: isDark?"rgba(255,255,255,0.05)":"rgba(30,139,175,0.07)",
              }}>
                <Typography sx={{ color: isDark?"#90caf9":accent, fontWeight:700, fontSize:"11px" }}>{folder.all}</Typography>
                <Typography sx={{ color: textSecondary, fontSize:"11px" }}>Total</Typography>
              </Box>
            </Box>

            {/* Subfolders label chips */}
            {hasSubs && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{
                  color: textSecondary, fontSize: "10px", fontWeight: 700,
                  letterSpacing: "0.08em", textTransform: "uppercase", display: "block", mb: 0.8,
                }}>
                  Subfolders
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                  {folder.subfolders.map(sf => (
                    <Chip key={sf.id} label={`📂 ${sf.name}`} size="small" sx={{
                      height: "22px", fontSize: "11px", fontWeight: 500, borderRadius: "6px",
                      background: isDark?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.04)",
                      color: isDark?"#aaa":"#666", border:"none",
                      "& .MuiChip-label":{ px: 1 },
                    }} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Expanded subfolder rows */}
            <Collapse in={open}>
              {hasSubs && (
                <Box sx={{
                  mb: 2, borderRadius: "12px", overflow: "hidden",
                  background: isDark?"rgba(255,255,255,0.03)":"rgba(30,139,175,0.03)",
                  border: isDark?"1px solid rgba(255,255,255,0.06)":"1px solid rgba(30,139,175,0.08)",
                }}>
                  {folder.subfolders.map(sf => <SubfolderRow key={sf.id} sf={sf} depth={0} />)}
                </Box>
              )}
            </Collapse>

            {/* Divider */}
            <Box sx={{ height: "1px", background: isDark?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)", mb: 2 }} />

            {/* Action buttons */}
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant={sel ? "contained" : "outlined"}
                size="small" disableElevation fullWidth
                onClick={() => toggleFolder(folder)}
                sx={{
                  borderRadius: "10px", py: 1, fontSize: "12px", fontWeight: 700,
                  textTransform: "none", letterSpacing: "0.02em",
                  ...(sel ? {
                    background: "linear-gradient(135deg,#1e8baf 0%,#1679a0 100%)",
                    color: "#fff",
                    boxShadow: "0 4px 12px rgba(30,139,175,0.3)",
                    "&:hover": { background: "linear-gradient(135deg,#2299c0 0%,#1e8baf 100%)", boxShadow: "0 6px 18px rgba(30,139,175,0.4)" },
                  } : {
                    borderColor: isDark?"rgba(30,139,175,0.4)":"rgba(30,139,175,0.5)",
                    color: accent,
                    "&:hover": { borderColor: accent, background: "rgba(30,139,175,0.06)" },
                  }),
                }}
              >
                {sel ? "✓ Selected" : "Select"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  // ─── Shared dialog styles ────────────────────────────────────────────────
  const dlgPaper = {
    borderRadius: "20px", overflow: "hidden",
    background: isDark ? "#1a2230" : "#fff",
    boxShadow: isDark ? "0 8px 40px rgba(0,0,0,0.55)" : "0 8px 40px rgba(30,139,175,0.18)",
  };
  const dlgHeader = {
    background: "linear-gradient(135deg,#1e3a8a 0%,#1e8baf 100%)",
    p: 3, display: "flex", alignItems: "center", justifyContent: "space-between", color: "white",
  };
  const dlgContent = {
    p: 3, background: isDark ? "#1a2230" : "#fff",
  };
  const dlgActions = {
    p: 2.5, gap: 1.5, background: isDark ? "#1a2230" : "#fff",
    borderTop: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)",
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ padding: { xs: 2, sm: 3, md: 4 }, minHeight: "100vh", backgroundColor: isDark ? "#121212" : "#F5F5F5" }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: isDark ? "#fff" : "#2c3e50", letterSpacing: "-0.02em" }}>
            Flashcard Practice
          </Typography>
          <Typography variant="body2" sx={{ color: textSecondary, mt: 0.4 }}>
            Select folders, apply tag filters, and start practicing
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Switch checked={spaceRepetitionEnabled} onChange={e => setSpaceRepetitionEnabled(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: accent },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: accent },
                }} />
            }
            label={<Typography sx={{ fontWeight: 500, fontSize: "14px", color: isDark ? "#ccc" : "#444" }}>Space Repetition</Typography>}
            sx={{ mr: 0 }}
          />
          {spaceRepetitionEnabled && (
            <Tooltip title="Configure Space Repetition">
              <IconButton onClick={() => setSettingsOpen(true)} sx={{
                color: isDark ? "#90caf9" : accent, background: isDark ? "rgba(144,202,249,0.1)" : "rgba(30,139,175,0.08)",
                "&:hover": { background: isDark ? "rgba(144,202,249,0.2)" : "rgba(30,139,175,0.15)" }, "&:focus": { outline: "none" },
              }}><SettingsIcon /></IconButton>
            </Tooltip>
          )}
          <Tooltip title="User Guide">
            <IconButton onClick={() => setUserGuideOpen(true)} sx={{
              color: isDark ? "#90caf9" : accent, background: isDark ? "rgba(144,202,249,0.1)" : "rgba(30,139,175,0.08)",
              "&:hover": { background: isDark ? "rgba(144,202,249,0.2)" : "rgba(30,139,175,0.15)" }, "&:focus": { outline: "none" },
            }}><InfoIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <Box sx={{
        display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 3,
        p: 2, borderRadius: "14px",
        background: isDark ? "rgba(255,255,255,0.03)" : "rgba(30,139,175,0.04)",
        border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(30,139,175,0.12)",
      }}>
        <TextField
          size="small" placeholder="Search folders..." value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: isDark?"#90caf9":accent, fontSize: 20 }} /></InputAdornment> }}
          sx={{
            flex: 1, minWidth: "180px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px", backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(30,139,175,0.04)",
              "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(30,139,175,0.2)" },
            },
          }}
        />
        <TextField select size="small" value={sort} onChange={e => setSort(e.target.value)}
          sx={{ minWidth: "160px", "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: isDark?"rgba(255,255,255,0.05)":"rgba(30,139,175,0.04)" } }}>
          {SORT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>

        <Button size="small" variant="outlined" onClick={() => setSelectedFolderIds(getAllIds())}
          sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: "13px", color: accent, borderColor: accent, "&:hover": { background: "rgba(30,139,175,0.07)" } }}>
          Select All
        </Button>
        <Button size="small" variant="outlined" onClick={() => setSelectedFolderIds([])}
          sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: "13px", color: isDark?"#f48fb1":"#d32f2f", borderColor: isDark?"#f48fb1":"#d32f2f", "&:hover": { background: "rgba(211,47,47,0.06)" } }}>
          Clear
        </Button>

        <Button variant="outlined" size="small" startIcon={<VisibilityIcon />} disabled={totalSelected === 0} onClick={handleViewDueCards}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: "13px",
            color: isDark ? "#90caf9" : accent, borderColor: isDark ? "#90caf9" : accent,
            "&:hover": { background: isDark ? "rgba(144,202,249,0.1)" : "rgba(30,139,175,0.07)" },
            "&.Mui-disabled": { opacity: 0.4 },
          }}>
          View Due Cards
        </Button>

        <Badge badgeContent={totalSelected > 0 ? totalSelected : null} color="primary">
          <Button variant="contained" size="small" startIcon={<PlayArrowIcon />}
            disabled={totalSelected === 0} onClick={() => setPracticeDialogOpen(true)} disableElevation
            sx={{
              borderRadius: "10px", textTransform: "none", fontWeight: 700, fontSize: "13px",
              background: "linear-gradient(135deg,#1e3a8a 0%,#1e8baf 100%)",
              boxShadow: "0 4px 14px rgba(30,139,175,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#2299c0 0%,#1e8baf 100%)", boxShadow: "0 6px 20px rgba(30,139,175,0.45)" },
              "&.Mui-disabled": { opacity: 0.4, background: "#888" },
            }}>
            Practice
          </Button>
        </Badge>
      </Box>

      {/* ── Selection strip ──────────────────────────────────────────────── */}
      {totalSelected > 0 && (
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1.5, mb: 2.5, px: 2.5, py: 1.5,
          borderRadius: "12px",
          background: isDark ? "rgba(30,139,175,0.12)" : "rgba(30,139,175,0.07)",
          border: `1px solid ${isDark ? "rgba(30,139,175,0.3)" : "rgba(30,139,175,0.2)"}`,
        }}>
          <CheckBoxIcon sx={{ color: accent, fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: isDark ? "#90caf9" : accent, fontWeight: 600 }}>
            {totalSelected} folder{totalSelected !== 1 ? "s" : ""} selected
          </Typography>
          {isFetchingTags && <>
            <CircularProgress size={14} sx={{ color: accent, ml: 1 }} />
            <Typography variant="caption" sx={{ color: textSecondary }}>Loading tags…</Typography>
          </>}
          {!isFetchingTags && availableTags.length > 0 && (
            <Typography variant="caption" sx={{ color: textSecondary }}>
              · {availableTags.length} tag{availableTags.length !== 1 ? "s" : ""} available
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setSelectedFolderIds([])}
            sx={{ textTransform: "none", fontSize: "12px", color: isDark ? "#f48fb1" : "#d32f2f", fontWeight: 600, minWidth: 0 }}>
            Clear
          </Button>
        </Box>
      )}

      {/* ── Folder grid ─────────────────────────────────────────────────── */}
      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", my: 8, gap: 2 }}>
          <CircularProgress size={48} thickness={3} sx={{ color: accent }} />
          <Typography variant="body2" sx={{ color: textSecondary, letterSpacing: "0.05em" }}>Loading folders…</Typography>
        </Box>
      ) : displayFolders.length === 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", my: 10, gap: 2 }}>
          <Box sx={{ width: 72, height: 72, borderRadius: "20px", fontSize: "32px",
            background: isDark ? "linear-gradient(135deg,#1e2a35 0%,#1a3a4a 100%)" : "linear-gradient(135deg,#e8f4f8 0%,#d0eaf4 100%)",
            display: "flex", alignItems: "center", justifyContent: "center" }}>📁</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? "#ccc" : "#444" }}>No folders found</Typography>
          <Typography variant="body2" sx={{ color: textSecondary, textAlign: "center", maxWidth: 300 }}>
            {search ? `No folders match "${search}"` : "Create a folder to get started."}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {displayFolders.map((f, i) => <FolderCard key={f.id} folder={f} index={i} />)}
        </Grid>
      )}

      {/* ═══════════════ PRACTICE DIALOG ══════════════════════════════════ */}
      <Dialog open={practiceDialogOpen} onClose={() => setPracticeDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dlgPaper }}>
        <Box sx={dlgHeader}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.3 }}>Configure Practice</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {totalSelected} folder{totalSelected !== 1 ? "s" : ""} selected
            </Typography>
          </Box>
          <IconButton onClick={() => setPracticeDialogOpen(false)} sx={{ color: "white", "&:hover": { background: "rgba(255,255,255,0.12)" } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={dlgContent}>
          {/* Question Type */}
          <Typography variant="overline" sx={{ fontWeight: 700, color: isDark ? "#90caf9" : accent, letterSpacing: "0.1em", display: "block", mb: 1.5 }}>
            Question Type
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
            {QUESTION_TYPES.map(qt => {
              const active = activeQType === qt.value;
              return (
                <Chip key={qt.value} label={`${qt.icon} ${qt.label}`} onClick={() => setActiveQType(qt.value)} size="small"
                  sx={{
                    cursor: "pointer", fontWeight: active ? 700 : 500, borderRadius: "8px", height: "30px", fontSize: "12px",
                    background: active ? "linear-gradient(135deg,#1e3a8a 0%,#1e8baf 100%)" : isDark ? "rgba(255,255,255,0.07)" : "rgba(30,139,175,0.07)",
                    color: active ? "#fff" : isDark ? "#90caf9" : accent,
                    border: active ? "1.5px solid #1e8baf" : isDark ? "1.5px solid rgba(255,255,255,0.12)" : "1.5px solid rgba(30,139,175,0.2)",
                    transition: "all 0.18s",
                    "&:hover": { background: active ? "linear-gradient(135deg,#2299c0 0%,#1e8baf 100%)" : isDark ? "rgba(255,255,255,0.12)" : "rgba(30,139,175,0.13)" },
                    "& .MuiChip-label": { px: 1.2 },
                  }}
                />
              );
            })}
          </Box>

          <Divider sx={{ mb: 2.5, borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)" }} />

          {/* Tags */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.2 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, color: isDark ? "#90caf9" : accent, letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 0.7 }}>
              <LocalOfferIcon sx={{ fontSize: 15 }} /> Filter by Tags
            </Typography>
            {availableTags.length > 0 && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" onClick={() => setSelectedTagIds(availableTags.map(t => t.id))}
                  sx={{ textTransform: "none", fontSize: "11px", color: accent, fontWeight: 700, p: "2px 8px", minWidth: 0 }}>All</Button>
                <Button size="small" onClick={() => setSelectedTagIds([])}
                  sx={{ textTransform: "none", fontSize: "11px", color: textSecondary, fontWeight: 600, p: "2px 8px", minWidth: 0 }}>None</Button>
              </Box>
            )}
          </Box>

          {isFetchingTags ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2.5 }}>
              <CircularProgress size={18} sx={{ color: accent }} />
              <Typography variant="body2" sx={{ color: textSecondary }}>Loading tags…</Typography>
            </Box>
          ) : availableTags.length === 0 ? (
            <Box sx={{ py: 2, px: 2.5, borderRadius: "12px", textAlign: "center",
              background: isDark ? "rgba(255,255,255,0.03)" : "rgba(30,139,175,0.04)",
              border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(30,139,175,0.1)" }}>
              <Typography variant="body2" sx={{ color: textSecondary }}>
                No tags found — all questions in selected folders will be included.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 1, maxHeight: "180px", overflowY: "auto",
                p: 1.5, borderRadius: "12px",
                background: isDark ? "rgba(255,255,255,0.03)" : "rgba(30,139,175,0.03)",
                border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(30,139,175,0.1)",
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-thumb": { background: isDark ? "#475569" : "#b0bec5", borderRadius: "6px" },
              }}>
                {availableTags.map(tag => {
                  const tSel = selectedTagIds.includes(tag.id);
                  return (
                    <Chip key={tag.id} label={tag.name}
                      icon={<LocalOfferIcon sx={{ fontSize: "13px !important", color: tSel ? "#fff !important" : undefined }} />}
                      onClick={() => setSelectedTagIds(prev => tSel ? prev.filter(id => id !== tag.id) : [...prev, tag.id])}
                      size="small"
                      sx={{
                        cursor: "pointer", fontWeight: tSel ? 700 : 500, borderRadius: "8px", height: "28px", fontSize: "12px",
                        background: tSel ? "linear-gradient(135deg,#1e3a8a 0%,#1e8baf 100%)" : isDark ? "rgba(255,255,255,0.07)" : "rgba(30,139,175,0.07)",
                        color: tSel ? "#fff" : isDark ? "#90caf9" : accent,
                        border: tSel ? "1.5px solid #1e8baf" : isDark ? "1.5px solid rgba(255,255,255,0.1)" : "1.5px solid rgba(30,139,175,0.18)",
                        transition: "all 0.18s",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  );
                })}
              </Box>
              <Typography variant="caption" sx={{ mt: 1, display: "block", color: selectedTagIds.length > 0 ? accent : textSecondary, fontWeight: 600 }}>
                {selectedTagIds.length > 0
                  ? `${selectedTagIds.length} tag${selectedTagIds.length !== 1 ? "s" : ""} selected`
                  : "No tags selected — all questions included"}
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={dlgActions}>
          <Button onClick={() => setPracticeDialogOpen(false)} variant="outlined"
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: isDark?"#90caf9":accent, borderColor: isDark?"#90caf9":accent,
              "&:hover": { background: isDark?"rgba(144,202,249,0.08)":"rgba(30,139,175,0.07)" } }}>
            Cancel
          </Button>
         <Button
  onClick={handleStartPractice}
  variant="contained"
  disableElevation
  startIcon={isFetchingQ ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <PlayArrowIcon />}
  disabled={isFetchingQ } 
  sx={{
    borderRadius: "10px", textTransform: "none", fontWeight: 700, px: 3,
    background: "linear-gradient(135deg,#1e3a8a 0%,#1e8baf 100%)",
    boxShadow: "0 4px 14px rgba(30,139,175,0.35)",
    "&:hover": { background: "linear-gradient(135deg,#2299c0 0%,#1e8baf 100%)", boxShadow: "0 6px 20px rgba(30,139,175,0.45)" },
    "&.Mui-disabled": { background: "#555", color: "#aaa" },
  }}
>
  {isFetchingQ ? "Loading…" : "Start Practice"}
</Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════ SETTINGS DIALOG ══════════════════════════════════ */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} PaperProps={{ sx: { ...dlgPaper, maxWidth: "480px", width: "100%" } }}>
        <Box sx={dlgHeader}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "white" }}>Space Repetition Settings</Typography>
          <IconButton onClick={() => setSettingsOpen(false)} sx={{ color: "white" }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={dlgContent}>
          {Object.entries(reviewSettings).map(([key, val]) => (
            <TextField key={key} fullWidth margin="dense" type="number"
              label={key.replaceAll("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
              value={val}
              onChange={e => setReviewSettings({ ...reviewSettings, [key]: Number(e.target.value) })}
              InputProps={{ sx: { borderRadius: "10px", background: isDark ? "rgba(255,255,255,0.05)" : "rgba(30,139,175,0.04)" } }}
              InputLabelProps={{ sx: { color: isDark ? "rgba(144,202,249,0.8)" : "rgba(0,0,0,0.55)", fontWeight: 500 } }}
              sx={{ mb: 1 }}
            />
          ))}
        </DialogContent>
        <DialogActions sx={dlgActions}>
          <Button onClick={() => setSettingsOpen(false)} variant="outlined"
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: isDark?"#90caf9":accent, borderColor: isDark?"#90caf9":accent }}>
            Cancel
          </Button>
          <Button onClick={handleSaveSettings} variant="contained" disableElevation
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700,
              background: "linear-gradient(135deg,#1e3a8a 0%,#1e8baf 100%)",
              "&:hover": { background: "linear-gradient(135deg,#2299c0 0%,#1e8baf 100%)" } }}>
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════ USER GUIDE DIALOG ════════════════════════════════ */}
      <Dialog open={userGuideOpen} onClose={() => setUserGuideOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: dlgPaper }}>
        <Box sx={dlgHeader}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.3 }}>User Guide</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>Flashcard Practice</Typography>
          </Box>
          <IconButton onClick={() => setUserGuideOpen(false)} sx={{ color: "white" }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{
          ...dlgContent, maxHeight: "70vh", overflowY: "auto",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": { background: isDark ? "#475569" : "#b0bec5", borderRadius: "6px" },
        }}>
          {[
            { icon: "📁", title: "Select Folders", desc: 'Click "Select" on any folder card to include it. Selecting a parent folder automatically selects all its subfolders. Expand a folder to individually select/deselect subfolders. Use "Select All" in the toolbar to select everything at once.' },
            { icon: "▶️", title: "Start Practice", desc: 'Once folders are selected, click the "Practice" button (shows count badge). A dialog lets you pick question type and tag filters before launching.' },
            { icon: "🏷️", title: "Tag Filtering", desc: 'Tags are automatically fetched from your selected folders. Pick specific tags to narrow the question set, or leave all unselected to include every question. "All" and "None" shortcuts are available.' },
            { icon: "📝", title: "Question Types", desc: 'Choose All Types, Multiple Choice (MCQ), Fill in Blank (FIB), True/False, or Image Based. The API filters questions by your selection.' },
            { icon: "👁️", title: "View Due Cards", desc: '"View Due Cards" in the toolbar shows flashcards scheduled for spaced-repetition review based on your learning history.' },
            { icon: "🔁", title: "Space Repetition", desc: 'Toggle Space Repetition in the header to enable SRS. Click the gear icon to set custom intervals for each recall rating (forgot, partial, effort, easy).' },
            { icon: "🔍", title: "Search & Sort", desc: 'Filter the folder list by name with the search bar. Sort alphabetically or by question count to navigate large libraries quickly.' },
          ].map(({ icon, title, desc }) => (
            <Box key={title} sx={{ mb: 3.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.8 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: "10px", fontSize: "18px", flexShrink: 0,
                  background: "linear-gradient(135deg,#1e3a8a 0%,#1e8baf 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? "#e0e0e0" : "#1a2a3a", fontSize: "15px" }}>{title}</Typography>
              </Box>
              <Typography variant="body2" sx={{ pl: 7, color: isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.6)", lineHeight: 1.75 }}>
                {desc}
              </Typography>
            </Box>
          ))}
          <Box sx={{ p: 3, borderRadius: "12px",
            background: isDark ? "rgba(144,202,249,0.05)" : "rgba(30,139,175,0.05)",
            border: isDark ? "1px solid rgba(144,202,249,0.15)" : "1px solid rgba(30,139,175,0.15)" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? "#90caf9" : accent, mb: 1.5 }}>💡 Quick Tips</Typography>
            {[
              "Selecting a parent folder auto-selects all its subfolders.",
              "Leave tags empty to practice all questions across selected folders.",
              "Combine tag filtering + question type for laser-focused sessions.",
              "Enable Space Repetition for science-backed long-term retention.",
              "Check View Due Cards regularly to stay on top of your schedule.",
            ].map(tip => (
              <Typography key={tip} variant="body2"
                sx={{ color: isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.6)", lineHeight: 1.75, mb: 0.5 }}>
                • {tip}
              </Typography>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Snackbar ────────────────────────────────────────────────────── */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}